import { FC } from "react";
import { useSelector } from "react-redux";

import { Button, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { PaymentFailedAlert, PaymentFailedLocation } from "components/payment-failures-limits/PaymentFailedAlert";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { changeIsValid, hasSubscription as hasSubscriptionHelper } from "helpers/subscription";
import { isDowngrade } from "helpers/subscription-plans";
import { useBreakpoints } from "hooks/use-breakpoints";
import { useMacros } from "hooks/use-macros";
import { selectStandardPlansLimits } from "store/entities/application-settings/selectors";
import { getBilling } from "store/entities/billing";
import { selectActiveCustomFieldsList } from "store/entities/custom-fields/selectors";
import { selectCustomEmailDomains } from "store/entities/email-domains";
import { selectEmailTemplatesList } from "store/entities/email-template/selectors";
import { selectMailboxesAlphabetically } from "store/entities/mailboxes/selectors";
import { IRecurlyCoupon, getIsCheckingRecurlyCoupon, getIsFetchingCoupons } from "store/entities/recurly";
import { selectReplyAddressesAlphabetically } from "store/entities/reply-addresses/selectors";
import { selectActiveRules } from "store/entities/rules/selectors";
import { BillingCycle, ISubscription, selectCurrentSubscription } from "store/entities/subscriptions";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { AgentsRow } from "./AgentsRow";
import { BillingCycleRow } from "./BillingCycleRow";
import { CouponRow } from "./CouponRow";
import { DowngradeAlerts } from "./DowngradeAlerts";
import { SubscriptionChangePreview } from "./SubscriptionChangePreview";
import { StyledButtonRow, StyledLoader, StyledWrapper } from "./styles";

interface IProps {
  newSubscription: ISubscription;
  coupon: IRecurlyCoupon | null;
  nonViewersCount: number;
  isSavingSubscription: boolean;
  paymentFailed: boolean;
  onSubscriptionSave: () => void;
  onBillingCycleChange: (billingCycle: BillingCycle) => void;
  onSeatsChange: (seats: number) => void;
}

const PlanSummary: FC<IProps> = ({
  newSubscription,
  coupon,
  nonViewersCount,
  isSavingSubscription,
  paymentFailed,
  onSubscriptionSave,
  onSeatsChange,
  onBillingCycleChange,
}) => {
  const { sharedMacros } = useMacros();

  const { isMobile } = useBreakpoints();

  const teams = useSelector(selectTeamsAlphabetically);
  const forwardingAddresses = useSelector(selectMailboxesAlphabetically);
  const replyAddresses = useSelector(selectReplyAddressesAlphabetically);
  const fullyCustomDomains = useSelector(selectCustomEmailDomains);
  const emailTemplates = useSelector(selectEmailTemplatesList);
  const activeWorkflows = useSelector(selectActiveRules);
  const activeCustomFields = useSelector(selectActiveCustomFieldsList);
  const isCheckingRecurlyCoupon = useSelector(getIsCheckingRecurlyCoupon);
  const isFetchingCoupons = useSelector(getIsFetchingCoupons);
  const isLoadingCoupons = isCheckingRecurlyCoupon || isFetchingCoupons;
  const subscription = useSelector(selectCurrentSubscription);
  const billing = useSelector(getBilling);

  const {
    maxSharedMacros,
    maxTeams,
    maxMailboxes,
    maxReplyAddresses,
    maxEmailDomains,
    maxRulesActive,
    maxCustomFieldsActive,
    maxTemplatesPerLicense,
  } = useSelector(selectStandardPlansLimits)[newSubscription.plan];

  const teamsToRemove = teams.length - maxTeams;
  const customDomainsToRemove = fullyCustomDomains.length - maxEmailDomains;
  const emailTemplatesToRemove = emailTemplates.length - maxTemplatesPerLicense;
  const workflowsToDeactivate = activeWorkflows.length - maxRulesActive;
  const sharedMacrosToRemove = sharedMacros.length - maxSharedMacros;
  const customFieldsToDeactivate = activeCustomFields.length - maxCustomFieldsActive;
  const forwardingAddressesToRemove = forwardingAddresses.length + 1 - maxMailboxes; // we have to add 1 to the count of forwarding addresses because the default mailbox is not included in the list from the API
  const replyAddressesToRemove = replyAddresses.length + 1 - maxReplyAddresses; // we have to add 1 to the count of reply addresses because the default reply address is not included in the list from the API

  const hasSubscription = hasSubscriptionHelper(billing, subscription);
  const displayDowngradeAlerts =
    isDowngrade(subscription.plan, newSubscription.plan) &&
    (teamsToRemove > 0 ||
      forwardingAddressesToRemove > 0 ||
      replyAddressesToRemove > 0 ||
      customDomainsToRemove > 0 ||
      emailTemplatesToRemove > 0 ||
      workflowsToDeactivate > 0 ||
      sharedMacrosToRemove > 0 ||
      customFieldsToDeactivate > 0);
  const changeValid = subscription
    ? changeIsValid(billing, subscription, newSubscription, coupon, nonViewersCount)
    : true;
  const disableSubmit = isSavingSubscription || !changeValid || displayDowngradeAlerts;

  const handleSaveSubscription = (): void => {
    onSubscriptionSave();
    trackEngagement("Subscribe cta clicked", EventPlace.Subscription);
  };

  return (
    <StyledWrapper>
      <AgentsRow newSubscription={newSubscription} nonViewersCount={nonViewersCount} onSeatsChange={onSeatsChange} />
      <BillingCycleRow
        subscription={subscription}
        newSubscription={newSubscription}
        onBillingCycleChange={onBillingCycleChange}
      />
      {isLoadingCoupons ? (
        <StyledLoader />
      ) : (
        <>
          {coupon && <CouponRow coupon={coupon} />}
          <SubscriptionChangePreview
            subscription={subscription}
            newSubscription={newSubscription}
            coupon={coupon}
            css={{
              marginTop: withVar(SpacingToken.Spacing5),
            }}
          />
        </>
      )}
      {displayDowngradeAlerts && (
        <DowngradeAlerts
          teamsToRemove={teamsToRemove}
          forwardingAddressesToRemove={forwardingAddressesToRemove}
          replyAddressesToRemove={replyAddressesToRemove}
          customDomainsToRemove={customDomainsToRemove}
          emailTemplatesToRemove={emailTemplatesToRemove}
          workflowsToDeactivate={workflowsToDeactivate}
          sharedMacrosToRemove={sharedMacrosToRemove}
          customFieldsToDeactivate={customFieldsToDeactivate}
        />
      )}
      {paymentFailed && (
        <Block marginTop={withVar(SpacingToken.Spacing4)}>
          <PaymentFailedAlert location={PaymentFailedLocation.SubUpdate} />
        </Block>
      )}
      <StyledButtonRow>
        <Button
          fullWidth={isMobile}
          kind="primary"
          disabled={disableSubmit}
          onClick={handleSaveSubscription}
          data-testid="update-subscription"
        >
          {hasSubscription && (isSavingSubscription ? "Updating…" : "Update subscription")}
          {!hasSubscription && "Subscribe now"}
        </Button>
      </StyledButtonRow>
    </StyledWrapper>
  );
};

export { PlanSummary, type IProps };
